import { Component, inject, ChangeDetectionStrategy } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDatepickerModule } from '@angular/material/datepicker';

import { HR_MEAL_KINDS, HR_MEAL_KIND_TRAINING, HrMealKindDef } from '@app/shared/_models/hr';
import { addDays } from '../../hr-schedule-engine';

// Un salarié proposé à la saisie, avec ce que l'on sait de sa journée : est-il seulement au
// travail ce jour-là ? A-t-il déjà un repas saisi ? C'est ce qui évite de cocher au hasard.
export interface MealDialogEmployee {
	id: number;
	name: string;
	working: boolean;			// travaille sur au moins un jour de la période choisie
	already: boolean;			// a déjà un repas d'entreprise sur au moins un de ces jours
}

export interface MealDialogData {
	datestart: string;
	dateend: string;
	employees: MealDialogEmployee[];
	preselected: number[];
	// Bornes du mois affiché : la saisie s'y limite, comme le reste de l'écran.
	min: string;
	max: string;
}

export interface MealDialogResult {
	employees: number[];
	datestart: string;
	dateend: string;
	kind: number;
	comment: string;
}

// SAISIE D'UN REPAS PAYÉ PAR L'ENTREPRISE — le geste réel, tel qu'il se produit : « formation SAV
// les 12 et 13, déjeuner compris, pour Ana, Sema et Lisa ». UNE décision, UNE saisie ; le serveur
// se charge de poser les six lignes (salarié × jour) et d'écarter les titres correspondants.
@Component({
	selector: 'app-hr-meal-dialog',
	standalone: true,
	imports: [FormsModule, MatDialogModule, MatFormFieldModule, MatInputModule, MatSelectModule,
		MatButtonModule, MatIconModule, MatCheckboxModule, MatTooltipModule, MatDatepickerModule],
	templateUrl: './meal-dialog.component.html',
	changeDetection: ChangeDetectionStrategy.Eager,
	styleUrl: './meal-dialog.component.scss',
})
export class MealDialogComponent {

	public data: MealDialogData = inject(MAT_DIALOG_DATA);
	private dialogRef = inject(MatDialogRef<MealDialogComponent>);

	public readonly kinds: HrMealKindDef[] = HR_MEAL_KINDS;
	public kind = HR_MEAL_KIND_TRAINING;
	public comment = '';
	public datestart = '';
	public dateend = '';
	public selected = new Set<number>();

	constructor() {
		this.datestart = this.data.datestart;
		this.dateend = this.data.dateend;
		for (const id of this.data.preselected ?? []) this.selected.add(id);
	}

	// Les jours réellement couverts par la saisie — affichés en clair, parce que « du 12 au 13 »
	// pour trois personnes fait six lignes, et que personne ne doit le découvrir après coup.
	get days(): string[] {
		const out: string[] = [];
		if (!this.datestart || !this.dateend || this.dateend < this.datestart) return out;
		for (let d = this.datestart; d <= this.dateend && out.length < 62; d = addDays(d, 1)) out.push(d);
		return out;
	}

	get count(): number {
		return this.selected.size * this.days.length;
	}

	get valid(): boolean {
		return this.selected.size > 0 && this.days.length > 0;
	}

	toggle(id: number): void {
		if (this.selected.has(id)) this.selected.delete(id); else this.selected.add(id);
	}

	// « Tous ceux qui travaillent » : le cas le plus courant d'un repas d'équipe ou d'une fermeture
	// pour formation. Cocher trente cases à la main pour ça serait une punition.
	selectWorking(): void {
		this.selected = new Set(this.data.employees.filter(e => e.working).map(e => e.id));
	}

	clearSelection(): void {
		this.selected = new Set();
	}

	onDate(which: 'start' | 'end', value: any): void {
		const iso = this.isoOf(value);
		if (!iso) return;
		if (which === 'start') {
			this.datestart = iso;
			if (this.dateend < iso) this.dateend = iso;
		} else {
			this.dateend = iso;
			if (iso < this.datestart) this.datestart = iso;
		}
	}

	// Le datepicker rend une Date locale : on ne garde que le jour, sans heure ni fuseau — les
	// repas se comptent en jours métier, jamais en instants.
	private isoOf(v: any): string | null {
		if (!v) return null;
		if (typeof v === 'string') return v.slice(0, 10);
		const d = v as Date;
		if (isNaN(d.getTime())) return null;
		return d.getFullYear() + '-' + ('' + (d.getMonth() + 1)).padStart(2, '0') + '-' + ('' + d.getDate()).padStart(2, '0');
	}

	dateOf(iso: string): Date | null {
		return iso ? new Date(+iso.slice(0, 4), +iso.slice(5, 7) - 1, +iso.slice(8, 10)) : null;
	}

	get minDate(): Date | null { return this.dateOf(this.data.min); }
	get maxDate(): Date | null { return this.dateOf(this.data.max); }

	dayLabel(dat: string): string {
		return dat.slice(8, 10) + '/' + dat.slice(5, 7);
	}

	onValidate(): void {
		if (!this.valid) return;
		const result: MealDialogResult = {
			employees: [...this.selected],
			datestart: this.datestart,
			dateend: this.dateend,
			kind: this.kind,
			comment: (this.comment ?? '').trim(),
		};
		this.dialogRef.close(result);
	}

	onCancel(): void {
		this.dialogRef.close();
	}
}
